// The app's in-memory state: a tiny external store read with useSyncExternalStore.
// IndexedDB is the durable copy; actions.ts keeps the two in step.
import { useSyncExternalStore } from 'react';
import type { CardRow, Inbox, LessonProgressRow, NotebookDone, SharedSettings, SharedState, UserInfo } from '@valyrio/engine/api';
import type { PaceConfig, UiLang } from '@valyrio/engine/types';
import type { LocalFlag, OutboxWrite } from '../lib/db';
import type { Dictionary } from '../lib/dictionary';
import type { CourseWord } from '../lib/engine';
import { FALLBACK_PACE } from '../lib/shared';
import type { SyncStatus } from '../lib/sync';

export type Theme = UserInfo['theme'];

/** Where "me" came from: the API, the device cache, the dev demo, or nothing yet. */
export type Identity = 'server' | 'cached' | 'demo' | 'local';

export interface AppState {
  phase: 'loading' | 'ready';
  identity: Identity;
  me: UserInfo;
  /** The other person, from /api/me (names always come from here and `me`, never from the code). */
  /** `coach`: the partner's Ääntämisvalmentaja is on (MeResponse.partner.coach; coach/CoachVerdict.tsx). */
  partner: {
    id: string;
    name: string;
    role?: UserInfo['role'];
    coach?: boolean;
    /** Roosan korva: may ask about their takes (coach/rec/prefs.ts). */ recCoachEar?: boolean;
  } | null;
  /**
   * The server answered 401 login_required: this device has no valid session. The shell shows the
   * login screen (shell/Login.tsx) instead of the app until a login reloads the page.
   */
  needsLogin: boolean;
  pace: PaceConfig;
  /** Reminder time: one value for both, set by the admin (MeResponse.settings). */
  settings: SharedSettings;
  /** Last shared state from the server (or an estimate). Use useShared() for the live view. */
  shared: SharedState | null;
  sharedAt: string | null;
  dict: Dictionary | null;
  bundleSource: 'bundle' | 'fixture' | null;
  /**
   * 'offline': this device has no dictionary yet and the network is not reachable (the bundle is
   * kept only in IndexedDB, never in the service worker's cache); it is fetched when the
   * connection returns. Null otherwise.
   */
  dictMissing: 'offline' | null;
  /** Course word order (null until /data/course-order.json has been seen). */
  course: CourseWord[] | null;
  cards: Map<string, CardRow>; // key = `${type}|${itemId}`
  lessonProgress: LessonProgressRow[];
  /** This device's own count for today; notebook = notebook tasks marked done (a study day). */
  todayLocal: { date: string; reviews: number; newWords: number; notebook?: number };
  localFlags: Map<string, LocalFlag>;
  /** Notebook tasks this person marked done on this device (recent days; kv 'notebook'). */
  notebookDone: NotebookDone[];
  /** What waits for this person (from the last sync; null before the first). */
  inbox: Inbox | null;
  /** Queued writes the server refused for good (kv deadWrites), shown where they were made. */
  rejected: OutboxWrite[];
  sync: SyncStatus;
  theme: Theme;
  lang: UiLang;
  /** True while a study session is on screen (the update prompt waits for it to end). */
  inSession: boolean;
  /**
   * The device's storage: 'ok'; 'memory' = IndexedDB could not be opened, nothing is kept after a
   * reload; 'blocked' = another tab holds an older version of the database open.
   */
  storage: 'ok' | 'memory' | 'blocked';
  /** Grades not yet written to IndexedDB (a failed write is retried; the session says so). */
  unsaved: number;
  /** Cards left in today's session on screen (the app badge uses it instead of rebuilding). */
  sessionLeft: number | null;
}

/** Before the first /api/me: no reminder time. */
export const DEFAULT_SETTINGS: SharedSettings = { reminderTime: null };

function readLocal<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

function initialState(): AppState {
  const lang = readLocal<UiLang>('valyrio:lang', ['fi', 'en'], 'fi');
  const theme = readLocal<Theme>('valyrio:theme', ['system', 'light', 'dark'], 'system');
  return {
    phase: 'loading',
    identity: 'local',
    me: { id: 'local', name: '', email: '', uiLang: lang, theme, role: 'member' },
    partner: null,
    needsLogin: false,
    pace: FALLBACK_PACE,
    settings: DEFAULT_SETTINGS,
    shared: null,
    sharedAt: null,
    dict: null,
    bundleSource: null,
    dictMissing: null,
    course: null,
    cards: new Map(),
    lessonProgress: [],
    todayLocal: { date: '', reviews: 0, newWords: 0 },
    localFlags: new Map(),
    notebookDone: [],
    inbox: null,
    rejected: [],
    sync: { state: 'idle', pending: 0, lastSync: null },
    theme,
    lang,
    inSession: false,
    storage: 'ok',
    unsaved: 0,
    sessionLeft: null,
  };
}

type Listener = () => void;
let state: AppState = initialState();
const listeners = new Set<Listener>();

export const store = {
  get: (): AppState => state,
  set(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)): void {
    const p = typeof patch === 'function' ? patch(state) : patch;
    state = { ...state, ...p };
    for (const l of listeners) l();
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  /** Tests only. */
  reset(): void {
    state = initialState();
  },
};

/** Read app state. Pass a selector that returns an existing reference (not a new object). */
export function useApp(): AppState;
export function useApp<T>(select: (s: AppState) => T): T;
export function useApp<T>(select?: (s: AppState) => T): T | AppState {
  return useSyncExternalStore(
    store.subscribe,
    () => (select ? select(state) : state),
    () => (select ? select(state) : state),
  );
}
